import React, { useState, useEffect } from "react";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormInput from "Common/Custom/FormInput";
import FormSelect from "Common/Custom/FormSelect";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import FormTextarea from "Common/Custom/FormTextarea";
import { NavLink } from "react-router-dom";
import { showError } from "utils/notifications";
import EditPurchaseItemTable from "./EditPurchaseItemTable";

const EditPurchaseItemForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  loading,
  supplierOptions = [],
  productOptions = [],
  title = "Update Purchase Item",
  fields = [],
}) => {
  const [supplier, setSupplier] = useState(values.supplier_id || "");
  const [items, setItems] = useState([]);


  useEffect(() => {
    if (values.supplier_id) {
      setSupplier(values.supplier_id);
    }
  }, [values.supplier_id]);
  // Load items after API response
  useEffect(() => {
    if (values.items?.length > 0 && productOptions.length > 0) {
      const mapped = values.items.map((i) => {
        const product = productOptions.find((p) => p.id === i.product_id);

        return {
          details_id: i.details_id,
          item_name: {
            value: i.product_id,
            label: product ? product.product_name : "Unknown",
          },
          stock: Number(product?.stock || null),
          quantity: Number(i.quantity || null),
          rate: Number(i.rate || null),
          total: Number(i.total || null),
        };
      });

      setItems(mapped);
    } else {
      setItems([{ details_id: null, item_name: null, stock: null, quantity: null, rate: null, total: null }]);
    }
  }, [values.items, productOptions]);

  const optionsSupplier = supplierOptions.map((s) => ({
    value: s.supplier_id,
    label: s.supplier_name,
  }));

  const optionsProduct = productOptions.map((p) => ({
    value: p.id,
    label: `${p.product_name} (Stock: ${p.stock})`,
    stock: p.stock,
  }));

  const handleSupplierChange = (e) => {
    setSupplier(e.target.value);
    handleChange(e);
  };

  const isSupplierSelected = !!supplier;

  const grandTotal = items.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  const prepareData = () => {
    const cleanItems = items.filter(
      (i) => i.item_name && i.item_name.value && i.quantity > 0 && i.rate > 0
    );
  
    const existingItems = values.items || [];
  
    const mapped = cleanItems.map((i, index) => {
      const old = existingItems[index]; // match using index, not product_id
  
      return {
        details_id: old ? old.details_id : null,
        product_id: i.item_name.value,
        quantity: Number(i.quantity),
        rate: Number(i.rate),
        total: Number(i.total),
        action: old ? "update" : "insert",
      };
    });
  
    // detect deleted rows
    const deletedRows = existingItems
      .slice(cleanItems.length)
      .map((old) => ({
        details_id: old.details_id,
        action: "delete",
      }));
  
    return {
      ...values,
      supplier_id: supplier,
      grand_total: grandTotal,
      items: [...mapped, ...deletedRows],
    };
  };
  
  

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const data = prepareData();

    if (!data.items.length) {
      return showError("Please add at least one valid item");
    }

    handleSubmit(data);
  };

  return (
    <PageBody
      onSubmit={handleFormSubmit}
      headerchildren={
        <>
          <HeadingOne>{title}</HeadingOne>
          <NavLink
            to="/admin/items-suppliers-list/add-supplier"
            className="btn btn-primary"
          >
            Create Supplier
          </NavLink>
        </>
      }
    >
      {fields.map((field) => {
        if (field.type === "select" && field.name === "supplier_id") {
          return (
            <FormSelect
              key={field.name}
              field={field}
              value={values[field.name] || supplier}
              handleChange={handleSupplierChange}
              options={optionsSupplier}
              error={errors[field.name]}
            />
          );
        }

        if (field.type === "date") {
          const isExpiry = field.name === "purchase_expiry_date";
          return (
            <FormDatePicker
              key={field.name}
              field={{
                ...field,
                minDate: isExpiry ? values.purchase_date : null,
              }}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
              dateFormat="Y-m-d"
              disabled={!isSupplierSelected}
            />
          );
        }

        if (field.type === "textarea") {
          return (
            <FormTextarea
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
              disabled={!isSupplierSelected}
            />
          );
        }

        return (
          <FormInput
            key={field.name}
            field={field}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
            disabled={!isSupplierSelected}
          />
        );
      })}

      <EditPurchaseItemTable
        items={items}
        setItems={setItems}
        optionsProduct={optionsProduct}
        isSupplierSelected={isSupplierSelected}
      />

      <div className="text-end fw-bold fs-5 mt-3">
        Grand Total: {grandTotal.toFixed(2)}
      </div>

      <FormSubmitButton
        label="Update"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

export default EditPurchaseItemForm;
